import { format } from "date-fns";
import { logEvent } from "helpers/analytics";
import { scrollToTop } from "lib/utils";
import React from "react";
import { FaUsers } from "react-icons/fa";
import { IconButton, Icon } from "@chakra-ui/react";
import { Share } from "lucide-react";

const employmentTypeToText = (type: string) => {
  switch (type) {
    case "FULL_TIME":
      return "Full Time";
    case "PART_TIME":
      return "Part Time";
    case "CONTRACT":
      return "Contract";
    default:
      return "Unknown";
  }
};

type JobAdSummaryCardProps = {
  isActive: boolean;
  setSelectedJobId: React.Dispatch<React.SetStateAction<string | null>>;
  title: string;
  employer: string;
  organization: string;
  visibility: string;
  jobAdId: string;
  datePosted: string | Date;
  location: {
    area: string;
    state: string;
    country: string;
  };
  onShare?: (jobId: string, jobTitle: string) => void;
};

export default function JobAdSummaryCard({
  isActive = false,
  title,
  employer,
  organization,
  visibility,
  jobAdId,
  datePosted,
  location,
  setSelectedJobId,
  onShare,
}: JobAdSummaryCardProps) {
  const handleCardClick = () => {
    logEvent("job_viewed_on_job_board", {
      job_id: jobAdId,
      job_title: title,
    });

    setSelectedJobId(jobAdId);
    scrollToTop();
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onShare) {
      onShare(jobAdId, title);
    }
  };

  return (
    <div
      className={`border-[#ACACAC] border-[1px] flex flex-col gap-3 px-4 py-6 hover:shadow-md hover:cursor-pointer relative ${
        isActive ? "bg-[#f1f0f0]" : ""
      }`}
      onClick={handleCardClick}
    >
      {onShare && (
        <div className="absolute top-4 right-4">
          <IconButton
            aria-label="Share job"
            icon={<Icon as={Share} w={4} h={4} />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            onClick={handleShareClick}
            _hover={{ bg: "gray.100" }}
          />
        </div>
      )}
      <div className="w-[70%] lg:w-[50%]">
        <h3 className="text-[#0E212D] text-[18px] lg:text-[23px] font-medium">
          {title}
        </h3>
      </div>
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-[#212121] text-[16px] lg:text-[20px]">
            {employer}
          </p>
          {visibility === "PRIVATE" && (
            <div className="flex items-center gap-2">
              <p className="text-[#212121] text-[16px] lg:text-[20px] font-normal">
                Exclusive to {organization} members
              </p>
              <FaUsers className="text-[#6E6E6E]" size={18} />
            </div>
          )}
          <p className="text-[#464646] text-[16px] lg:text-[20px]">
            Date posted:{" "}
            <span className="text-[#000000]">
              {format(new Date(datePosted), "PPPP")}
            </span>
          </p>
          <p className="text-[#828282] text-[16px] lg:text-[20px]">
            {`${location.state}, ${location.country}`}
          </p>
        </div>
      </div>
    </div>
  );
}
